/**
 * Blob Storage Connector configuration component.
 * Specialized configuration for Blob Storage connector (Azure / AWS S3).
 */

// React Imports
import { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Switch,
  TextField,
} from '@mui/material';

export interface BlobStorageConnectorConfigProps {
  settingsJson?: string;
  onChange?: (settingsJson: string) => void;
}

/**
 * Blob Storage Connector configuration component.
 */
export const BlobStorageConnectorConfig = ({
  settingsJson = '{}',
  onChange,
}: BlobStorageConnectorConfigProps) => {
  const [provider, setProvider] = useState<'azure' | 'aws'>('azure');
  const [defaultContainer, setDefaultContainer] = useState('');
  const [connectionString, setConnectionString] = useState('');
  const [accessKeyId, setAccessKeyId] = useState('');
  const [secretAccessKey, setSecretAccessKey] = useState('');
  const [region, setRegion] = useState('us-east-1');
  const [useManagedIdentity, setUseManagedIdentity] = useState(false);

  const handleProviderChange = (newProvider: 'azure' | 'aws') => {
    setProvider(newProvider);
    const settings = JSON.parse(settingsJson || '{}');
    settings.provider = newProvider;
    onChange?.(JSON.stringify(settings));
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Storage Provider Configuration
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Select Provider
          </Typography>
          <ToggleButtonGroup
            value={provider}
            exclusive
            onChange={(_, newProvider) => newProvider && handleProviderChange(newProvider)}
            fullWidth
          >
            <ToggleButton value="azure">Azure Blob Storage</ToggleButton>
            <ToggleButton value="aws">AWS S3</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {provider === 'azure' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={useManagedIdentity}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setUseManagedIdentity(e.target.checked);
                    const settings = JSON.parse(settingsJson || '{}');
                    settings.useManagedIdentity = e.target.checked;
                    onChange?.(JSON.stringify(settings));
                  }}
                />
              }
              label="Use Managed Identity (Azure)"
            />
            {!useManagedIdentity && (
              <TextField
                label="Connection String"
                type="password"
                value={connectionString}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setConnectionString(e.target.value);
                  const settings = JSON.parse(settingsJson || '{}');
                  settings.connectionString = e.target.value;
                  onChange?.(JSON.stringify(settings));
                }}
                fullWidth
                helperText="Azure Storage account connection string"
              />
            )}
            <TextField
              label="Default Container"
              value={defaultContainer}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setDefaultContainer(e.target.value);
                const settings = JSON.parse(settingsJson || '{}');
                settings.defaultContainer = e.target.value;
                onChange?.(JSON.stringify(settings));
              }}
              fullWidth
              helperText="Default container name"
            />
          </Box>
        )}

        {provider === 'aws' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Access Key ID"
              value={accessKeyId}
              onChange={(e) => {
                setAccessKeyId(e.target.value);
                const settings = JSON.parse(settingsJson || '{}');
                settings.accessKeyId = e.target.value;
                onChange?.(JSON.stringify(settings));
              }}
              fullWidth
            />
            <TextField
              label="Secret Access Key"
              type="password"
              value={secretAccessKey}
              onChange={(e) => {
                setSecretAccessKey(e.target.value);
                const settings = JSON.parse(settingsJson || '{}');
                settings.secretAccessKey = e.target.value;
                onChange?.(JSON.stringify(settings));
              }}
              fullWidth
            />
            <TextField
              label="Region"
              value={region}
              onChange={(e) => {
                setRegion(e.target.value);
                const settings = JSON.parse(settingsJson || '{}');
                settings.region = e.target.value;
                onChange?.(JSON.stringify(settings));
              }}
              fullWidth
              helperText="AWS region (e.g., us-east-1)"
            />
            <TextField
              label="Default Bucket"
              value={defaultContainer}
              onChange={(e) => {
                setDefaultContainer(e.target.value);
                const settings = JSON.parse(settingsJson || '{}');
                settings.defaultBucket = e.target.value;
                onChange?.(JSON.stringify(settings));
              }}
              fullWidth
              helperText="Default S3 bucket name"
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
